// Scans captured by the Perfusion Companion app and stored against a case (ENG-7381).
//
// `result`, `extras` and `errorData` are free-form jsonb on the backend: the app decides what a
// machine reading looks like, so the workspace renders whatever arrives instead of a fixed shape.

/** A frame stored in S3 alongside the scan. The list endpoint returns descriptors, not URLs. */
export interface IPerfusionScanFrame {
  bucket?: string;
  key?: string;
  region?: string;
  originalName?: string;
  contentType?: string;
  size?: number;
  index?: number;
  [extra: string]: unknown;
}

export interface IPerfusionScan {
  id: string;
  studyId: string;
  caseId: string;
  pumpCenter?: string | null;
  machineName: string;
  status: string;
  modelId?: string | null;
  result?: Record<string, unknown> | null;
  errorData?: Record<string, unknown> | null;
  frames?: IPerfusionScanFrame[] | null;
  extras?: Record<string, unknown> | null;
  capturedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IPerfusionScansResponse {
  scans: IPerfusionScan[];
  total: number;
  limit: number;
  offset: number;
}

export const PerfusionScanStatus = {
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED'
} as const;

/** One rendered row of a scan's collected values. */
export interface PerfusionScanValue {
  /** Human-readable label, e.g. `portalFlow` → "Portal flow". */
  label: string;
  /** Path into the source object, used as the row key. */
  path: string;
  value: string;
}

/** Nested readings deeper than this are shown as raw JSON rather than expanded further. */
const MAX_VALUE_DEPTH = 4;

const EMPTY_VALUE = '—';

/** All-caps words (and bare indices) are left alone so acronyms like `PH` survive. */
const isAcronym = (word: string) => word === word.toUpperCase();

export const humanizeScanKey = (key: string): string => {
  const words = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0);

  if (words.length === 0) {
    return key;
  }

  return words
    .map((word, index) => {
      if (isAcronym(word)) {
        return word;
      }

      return index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word.toLowerCase();
    })
    .join(' ');
};

export const formatScanValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return EMPTY_VALUE;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? `${value}` : EMPTY_VALUE;
  }

  if (typeof value === 'string') {
    return value.trim() || EMPTY_VALUE;
  }

  if (Array.isArray(value)) {
    const items = value.map(item => formatScanValue(item)).filter(item => item !== EMPTY_VALUE);
    return items.length > 0 ? items.join(', ') : EMPTY_VALUE;
  }

  return JSON.stringify(value);
};

/** True for anything rendered as a single row: scalars and arrays that hold only scalars. */
const isLeafValue = (value: unknown): boolean => {
  if (value === null || value === undefined || typeof value !== 'object') {
    return true;
  }

  return (
    Array.isArray(value) &&
    value.every(item => item === null || item === undefined || typeof item !== 'object')
  );
};

/**
 * Turns a scan's free-form jsonb into label/value rows, flattening nested objects into
 * `Parent › Child` labels so an unknown payload still reads as a list of readings.
 */
export const flattenScanValues = (source: unknown): PerfusionScanValue[] => {
  if (source === null || source === undefined) {
    return [];
  }

  if (typeof source === 'object' && Object.keys(source as object).length === 0) {
    return [];
  }

  const rows: PerfusionScanValue[] = [];

  const walk = (value: unknown, label: string, path: string, depth: number) => {
    if (isLeafValue(value) || depth >= MAX_VALUE_DEPTH) {
      rows.push({
        label: label || 'Value',
        path: path || 'value',
        value: formatScanValue(value)
      });
      return;
    }

    const entries: [string, unknown][] = Array.isArray(value) ?
      value.map((item, index) => [`${index + 1}`, item]) :
      Object.entries(value as Record<string, unknown>);

    if (entries.length === 0) {
      rows.push({ label: label || 'Value', path: path || 'value', value: EMPTY_VALUE });
      return;
    }

    for (const [key, child] of entries) {
      const childLabel = humanizeScanKey(key);
      walk(
        child,
        label ? `${label} › ${childLabel}` : childLabel,
        path ? `${path}.${key}` : key,
        depth + 1
      );
    }
  };

  walk(source, '', '', 0);
  return rows;
};

export class PerfusionScanModel {
  id: string;
  studyId: string;
  caseId: string;
  pumpCenter?: string;
  machineName: string;
  status: string;
  modelId?: string;
  result?: Record<string, unknown>;
  errorData?: Record<string, unknown>;
  frames: IPerfusionScanFrame[];
  extras?: Record<string, unknown>;
  capturedAt?: Date;
  createdAt: Date;
  updatedAt: Date;

  constructor(scan: IPerfusionScan) {
    this.id = scan.id;
    this.studyId = scan.studyId;
    this.caseId = scan.caseId;
    this.pumpCenter = scan.pumpCenter ?? undefined;
    this.machineName = scan.machineName;
    this.status = scan.status;
    this.modelId = scan.modelId ?? undefined;
    this.result = scan.result ?? undefined;
    this.errorData = scan.errorData ?? undefined;
    this.frames = scan.frames ?? [];
    this.extras = scan.extras ?? undefined;
    this.capturedAt = scan.capturedAt ? new Date(scan.capturedAt) : undefined;
    this.createdAt = new Date(scan.createdAt);
    this.updatedAt = new Date(scan.updatedAt);
  }

  static mapFromBackend(scan: IPerfusionScan): PerfusionScanModel {
    return new PerfusionScanModel(scan);
  }

  get isFailed(): boolean {
    return this.status === PerfusionScanStatus.FAILED;
  }

  /** When the reading was taken, falling back to when the server stored it. */
  get recordedAt(): Date {
    return this.capturedAt ?? this.createdAt;
  }

  get values(): PerfusionScanValue[] {
    return flattenScanValues(this.result);
  }

  get extraValues(): PerfusionScanValue[] {
    return flattenScanValues(this.extras);
  }

  get errorValues(): PerfusionScanValue[] {
    return flattenScanValues(this.errorData);
  }
}
